import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../app/ApiProvider";
import {
  Button,
  Card,
  Chip,
  ErrorNotice,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../components/ui";
import {
  buildPayload,
  deletionWarning,
  EMPTY_VIEW,
  formatBytes as formatBytesShort,
  formFromView,
  lastStatus,
  validateForm,
  type Destination,
  type OffsiteForm,
  type OffsiteView,
  type RemoteBackupFile,
} from "../lib/backup-offsite";

/**
 * Página de Backup y Restauración — solo visible para super-admin.
 *
 * Bloques verticales:
 *   1. Backup de base de datos  — generar, historial, descargar
 *   2. Copia fuera del servidor — SFTP, S3 o correo; cifrado opcional; retención configurable
 *   3. Restaurar base de datos  — subir .sql, validar, restaurar con confirmación
 *   4. Backup de .env           — descargar el .env.production actual
 *   5. Restaurar .env           — subir nuevo .env, validar diff, aplicar
 *
 * Los endpoints de restauración son asíncronos (guardian): se hace polling
 * hasta que el estado deja de ser "in_progress".
 */

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface BackupMeta {
  id: string;
  filename: string;
  createdAt: string;
  sizeBytes: number;
}

interface HistoryResponse {
  backups: BackupMeta[];
}

interface CreateBackupResponse {
  id: string;
  filename: string;
  createdAt: string;
  sizeBytes: number;
}

interface ValidateDbResponse {
  valid: boolean;
  tablesInFile: string[];
  tablesInDb: string[];
  migrationsInFile: string[];
  migrationsInDb: string[];
  missingInDb: string[];
  extraInDb: string[];
}

interface RestoreDbResponse {
  queued: boolean;
}

type RestoreStatus = "idle" | "in_progress" | "success" | "failed";

interface RestoreStatusResponse {
  status: RestoreStatus;
  queuedAt?: string;
  completedAt?: string;
  error?: string;
}

interface ValidateEnvResponse {
  valid: boolean;
  errors: string[];
  diff: {
    added: string[];
    removed: string[];
    changed: string[];
    unchanged: string[];
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusChip({ status }: { status: RestoreStatus }) {
  const map: Record<RestoreStatus, { cls: string; label: string }> = {
    idle: { cls: "chip chip-neutral", label: "Inactivo" },
    in_progress: { cls: "chip chip-warn", label: "En progreso…" },
    success: { cls: "chip chip-ok", label: "Completado" },
    failed: { cls: "chip chip-danger", label: "Error" },
  };
  const { cls, label } = map[status];
  return <span className={cls}>{label}</span>;
}

// ── Bloque 1: Backup BD ───────────────────────────────────────────────────────

function BackupDbSection() {
  const api = useApi();
  const [history, setHistory] = useState<BackupMeta[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createOk, setCreateOk] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryError(null);
    try {
      const res = await api.get<HistoryResponse>("/api/v1/internal/backup/history");
      setHistory(res.backups);
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : "Error al cargar historial");
    } finally {
      setLoadingHistory(false);
    }
  }, [api]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    setCreateOk(null);
    try {
      const res = await api.post<CreateBackupResponse>("/api/v1/internal/backup/db", {});
      setCreateOk(`Backup creado: ${res.filename} (${formatBytes(res.sizeBytes)})`);
      await loadHistory();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Error al crear backup");
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (confirmDeleteId !== id) {
      // Primer clic: pedir confirmación
      setConfirmDeleteId(id);
      // Auto-cancelar tras 4 s si no confirma
      setTimeout(() => setConfirmDeleteId((cur) => (cur === id ? null : cur)), 4000);
      return;
    }
    // Segundo clic: confirmar → borrar
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await api.delete(`/api/v1/internal/backup/${id}`);
      await loadHistory();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Error al eliminar backup");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDownload(id: string, filename: string) {
    setDownloadingId(id);
    try {
      const blob = await api.get<Blob>(
        `/api/v1/internal/backup/${id}/download`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="micro-label">Backup de base de datos</h2>
          <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
            Genera un volcado SQL comprimido. Se mantienen los últimos 5 backups.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => void handleCreate()}
          disabled={creating}
        >
          {creating ? "Generando…" : "Generar backup"}
        </Button>
      </div>

      {createError && (
        <div className="mb-3">
          <ErrorNotice error={new Error(createError)} />
        </div>
      )}
      {createOk && (
        <div className="mb-3">
          <Notice tone="ok">{createOk}</Notice>
        </div>
      )}

      {loadingHistory ? (
        <Spinner label="Cargando historial" />
      ) : historyError ? (
        <ErrorNotice
          error={new Error(historyError)}
          onRetry={() => void loadHistory()}
        />
      ) : history.length === 0 ? (
        <p className="py-4 text-center text-sm text-ink-400">
          No hay backups todavía. Genera el primero con el botón.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 dark:border-ink-700">
                <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                  Archivo
                </th>
                <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                  Fecha
                </th>
                <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                  Tamaño
                </th>
                <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
              {history.map((b) => (
                <tr key={b.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="py-2 pr-4 font-mono text-xs text-ink-700 dark:text-ink-200">
                    {b.filename}
                  </td>
                  <td className="py-2 pr-4 text-xs text-ink-600 dark:text-ink-300">
                    {formatDate(b.createdAt)}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-xs text-ink-500 dark:text-ink-400">
                    {formatBytes(b.sizeBytes)}
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => void handleDownload(b.id, b.filename)}
                        disabled={downloadingId === b.id || deletingId === b.id}
                        className="text-xs text-brand-600 underline hover:text-brand-700 disabled:opacity-50 dark:text-brand-400"
                      >
                        {downloadingId === b.id ? "Descargando…" : "Descargar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(b.id)}
                        disabled={deletingId === b.id || downloadingId === b.id}
                        className={`text-xs underline disabled:opacity-50 ${
                          confirmDeleteId === b.id
                            ? "font-semibold text-signal-danger dark:text-red-400"
                            : "text-ink-400 hover:text-signal-danger dark:text-ink-500 dark:hover:text-red-400"
                        }`}
                      >
                        {deletingId === b.id
                          ? "Eliminando…"
                          : confirmDeleteId === b.id
                          ? "¿Confirmar?"
                          : "Eliminar"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Bloque 2: Restaurar BD ────────────────────────────────────────────────────

function RestoreDbSection() {
  const api = useApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidateDbResponse | null>(null);
  const [validateError, setValidateError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [status, setStatus] = useState<RestoreStatus>("idle");
  const [statusDetail, setStatusDetail] = useState<RestoreStatusResponse | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cargar estado actual al montar
  useEffect(() => {
    void loadStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadStatus() {
    try {
      const res = await api.get<RestoreStatusResponse>(
        "/api/v1/internal/restore/db/status",
      );
      setStatus(res.status);
      setStatusDetail(res);
      if (res.status === "in_progress") {
        startPolling();
      }
    } catch {
      // silencioso: no bloquear la UI por esto
    }
  }

  function startPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<RestoreStatusResponse>(
          "/api/v1/internal/restore/db/status",
        );
        setStatus(res.status);
        setStatusDetail(res);
        if (res.status !== "in_progress") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      } catch {
        clearInterval(pollRef.current!);
        pollRef.current = null;
      }
    }, 2000);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setValidation(null);
    setValidateError(null);
    setConfirmed(false);
  }

  async function handleValidate() {
    if (!file) return;
    setValidating(true);
    setValidateError(null);
    setValidation(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.postForm<ValidateDbResponse>(
        "/api/v1/internal/restore/db/validate",
        formData,
      );
      setValidation(res);
    } catch (e) {
      setValidateError(e instanceof Error ? e.message : "Error al validar");
    } finally {
      setValidating(false);
    }
  }

  async function handleRestore() {
    if (!file || !validation?.valid || !confirmed) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.postForm<RestoreDbResponse>(
        "/api/v1/internal/restore/db",
        formData,
      );
      setStatus("in_progress");
      setStatusDetail({ status: "in_progress", queuedAt: new Date().toISOString() });
      setFile(null);
      setValidation(null);
      setConfirmed(false);
      if (fileRef.current) fileRef.current.value = "";
      startPolling();
    } catch (e) {
      setRestoreError(e instanceof Error ? e.message : "Error al encolar restauración");
    } finally {
      setRestoring(false);
    }
  }

  return (
    <Card>
      <h2 className="micro-label mb-1">Restaurar base de datos</h2>
      <p className="mb-4 text-xs text-ink-500 dark:text-ink-400">
        Sube un fichero <code className="rounded bg-ink-100 px-1 py-0.5 dark:bg-ink-800">.sql</code> o{" "}
        <code className="rounded bg-ink-100 px-1 py-0.5 dark:bg-ink-800">.sql.gz</code> para restaurar.
        La API se reiniciará automáticamente tras la operación.
      </p>

      {/* Estado actual del guardian */}
      {status !== "idle" && (
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-700 dark:text-ink-200">Estado:</span>
            <StatusChip status={status} />
            {statusDetail?.completedAt && (
              <span className="text-xs text-ink-500 dark:text-ink-400">
                — {formatDate(statusDetail.completedAt)}
              </span>
            )}
          </div>
          {statusDetail?.error && (
            <p className="mt-1 text-xs text-signal-danger dark:text-red-400">
              {statusDetail.error}
            </p>
          )}
          {status === "in_progress" && (
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400 animate-pulse">
              Guardian ejecutando restauración. Actualizando cada 2 s…
            </p>
          )}
        </div>
      )}

      {/* Selector de fichero */}
      <div className="space-y-3">
        <div>
          <label className="micro-label block mb-1" htmlFor="db-file-input">
            Fichero SQL
          </label>
          <input
            id="db-file-input"
            ref={fileRef}
            type="file"
            accept=".sql,.sql.gz,.gz"
            onChange={handleFileChange}
            className="block w-full text-sm text-ink-600 dark:text-ink-300 file:mr-3 file:rounded file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-950/50 dark:file:text-brand-300"
          />
        </div>

        {file && !validation && (
          <Button
            variant="quiet"
            onClick={() => void handleValidate()}
            disabled={validating}
          >
            {validating ? "Validando…" : "Validar fichero"}
          </Button>
        )}

        {validateError && <ErrorNotice error={new Error(validateError)} />}

        {/* Resultado de validación */}
        {validation && (
          <div className="rounded-lg border border-ink-200 p-3 dark:border-ink-700 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                Resultado de validación:
              </span>
              {validation.valid ? (
                <span className="chip chip-ok">Válido</span>
              ) : (
                <span className="chip chip-danger">Inválido</span>
              )}
            </div>

            <div className="grid gap-3 text-xs sm:grid-cols-2">
              <div>
                <p className="font-semibold text-ink-600 dark:text-ink-300 mb-1">
                  Tablas en el fichero ({validation.tablesInFile.length})
                </p>
                <ul className="space-y-0.5 text-ink-500 dark:text-ink-400 font-mono">
                  {validation.tablesInFile.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-ink-600 dark:text-ink-300 mb-1">
                  Tablas en BD actual ({validation.tablesInDb.length})
                </p>
                <ul className="space-y-0.5 text-ink-500 dark:text-ink-400 font-mono">
                  {validation.tablesInDb.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>

            {validation.missingInDb.length > 0 && (
              <Notice tone="warn">
                Tablas en el fichero que no existen en la BD actual:{" "}
                <span className="font-mono">{validation.missingInDb.join(", ")}</span>
              </Notice>
            )}
            {validation.extraInDb.length > 0 && (
              <Notice tone="neutral">
                Tablas en la BD actual que no están en el fichero:{" "}
                <span className="font-mono">{validation.extraInDb.join(", ")}</span>
              </Notice>
            )}
          </div>
        )}

        {/* Confirmación y restaurar */}
        {validation?.valid && (
          <div className="space-y-3">
            <Notice tone="danger">
              <strong>¡Atención!</strong> Esta operación reemplazará TODA la base de datos con el
              contenido del fichero. La API se reiniciará. Esta acción no se puede deshacer (salvo
              que tengas otro backup).
            </Notice>
            <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              Entiendo que esta operación es destructiva e irreversible
            </label>
            {restoreError && <ErrorNotice error={new Error(restoreError)} />}
            <Button
              variant="danger"
              onClick={() => void handleRestore()}
              disabled={!confirmed || restoring || status === "in_progress"}
            >
              {restoring ? "Encolando…" : "Restaurar base de datos"}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Bloque 3: Backup .env ─────────────────────────────────────────────────────

function BackupEnvSection() {
  const api = useApi();
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const blob = await api.get<Blob>(
        "/api/v1/internal/backup/env/download",
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ".env.production";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="micro-label">Backup de variables de entorno</h2>
          <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
            Descarga el fichero <code className="rounded bg-ink-100 px-1 py-0.5 dark:bg-ink-800">.env.production</code>{" "}
            actual. Guárdalo en un lugar seguro y cifrado.
          </p>
        </div>
        <Button
          variant="quiet"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? "Descargando…" : "Descargar .env"}
        </Button>
      </div>
    </Card>
  );
}

// ── Bloque 4: Restaurar .env ──────────────────────────────────────────────────

function RestoreEnvSection() {
  const api = useApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidateEnvResponse | null>(null);
  const [validateError, setValidateError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [status, setStatus] = useState<RestoreStatus>("idle");
  const [statusDetail, setStatusDetail] = useState<RestoreStatusResponse | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    void loadStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadStatus() {
    try {
      const res = await api.get<RestoreStatusResponse>(
        "/api/v1/internal/restore/env/status",
      );
      setStatus(res.status);
      setStatusDetail(res);
      if (res.status === "in_progress") startPolling();
    } catch {
      // silencioso
    }
  }

  function startPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<RestoreStatusResponse>(
          "/api/v1/internal/restore/env/status",
        );
        setStatus(res.status);
        setStatusDetail(res);
        if (res.status !== "in_progress") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      } catch {
        clearInterval(pollRef.current!);
        pollRef.current = null;
      }
    }, 2000);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setValidation(null);
    setValidateError(null);
    setConfirmed(false);
  }

  async function handleValidate() {
    if (!file) return;
    setValidating(true);
    setValidateError(null);
    setValidation(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.postForm<ValidateEnvResponse>(
        "/api/v1/internal/restore/env/validate",
        formData,
      );
      setValidation(res);
    } catch (e) {
      setValidateError(e instanceof Error ? e.message : "Error al validar");
    } finally {
      setValidating(false);
    }
  }

  async function handleRestore() {
    if (!file || !validation?.valid || !confirmed) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.postForm("/api/v1/internal/restore/env", formData);
      setStatus("in_progress");
      setStatusDetail({ status: "in_progress", queuedAt: new Date().toISOString() });
      setFile(null);
      setValidation(null);
      setConfirmed(false);
      if (fileRef.current) fileRef.current.value = "";
      startPolling();
    } catch (e) {
      setRestoreError(e instanceof Error ? e.message : "Error al encolar restauración");
    } finally {
      setRestoring(false);
    }
  }

  return (
    <Card>
      <h2 className="micro-label mb-1">Restaurar variables de entorno</h2>
      <p className="mb-4 text-xs text-ink-500 dark:text-ink-400">
        Sube un fichero <code className="rounded bg-ink-100 px-1 py-0.5 dark:bg-ink-800">.env</code> para
        reemplazar el <code className="rounded bg-ink-100 px-1 py-0.5 dark:bg-ink-800">.env.production</code>.
        La API se reiniciará automáticamente.
      </p>

      {/* Estado actual */}
      {status !== "idle" && (
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-700 dark:text-ink-200">Estado:</span>
            <StatusChip status={status} />
            {statusDetail?.completedAt && (
              <span className="text-xs text-ink-500 dark:text-ink-400">
                — {formatDate(statusDetail.completedAt)}
              </span>
            )}
          </div>
          {statusDetail?.error && (
            <p className="mt-1 text-xs text-signal-danger dark:text-red-400">
              {statusDetail.error}
            </p>
          )}
          {status === "in_progress" && (
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400 animate-pulse">
              Guardian aplicando nuevo .env. Actualizando cada 2 s…
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="micro-label block mb-1" htmlFor="env-file-input">
            Fichero .env
          </label>
          <input
            id="env-file-input"
            ref={fileRef}
            type="file"
            accept=".env,text/plain"
            onChange={handleFileChange}
            className="block w-full text-sm text-ink-600 dark:text-ink-300 file:mr-3 file:rounded file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-950/50 dark:file:text-brand-300"
          />
        </div>

        {file && !validation && (
          <Button
            variant="quiet"
            onClick={() => void handleValidate()}
            disabled={validating}
          >
            {validating ? "Validando…" : "Validar fichero"}
          </Button>
        )}

        {validateError && <ErrorNotice error={new Error(validateError)} />}

        {/* Resultado de validación */}
        {validation && (
          <div className="rounded-lg border border-ink-200 p-3 dark:border-ink-700 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                Resultado de validación:
              </span>
              {validation.valid ? (
                <span className="chip chip-ok">Válido</span>
              ) : (
                <span className="chip chip-danger">Inválido</span>
              )}
            </div>

            {/* Errores de validación Zod */}
            {validation.errors.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-signal-danger mb-1">
                  Variables inválidas o faltantes:
                </p>
                <ul className="space-y-0.5 text-xs font-mono text-signal-danger dark:text-red-400">
                  {validation.errors.map((e, i) => (
                    <li key={i}>• {e}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Diff de claves */}
            <div className="grid gap-2 text-xs sm:grid-cols-2">
              {validation.diff.added.length > 0 && (
                <div>
                  <p className="font-semibold text-signal-ok mb-0.5">
                    Claves añadidas ({validation.diff.added.length})
                  </p>
                  <ul className="font-mono text-ink-600 dark:text-ink-300 space-y-0.5">
                    {validation.diff.added.map((k) => (
                      <li key={k} className="text-signal-ok">+ {k}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.diff.removed.length > 0 && (
                <div>
                  <p className="font-semibold text-signal-danger mb-0.5">
                    Claves eliminadas ({validation.diff.removed.length})
                  </p>
                  <ul className="font-mono space-y-0.5">
                    {validation.diff.removed.map((k) => (
                      <li key={k} className="text-signal-danger dark:text-red-400">− {k}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.diff.changed.length > 0 && (
                <div>
                  <p className="font-semibold text-signal-warn mb-0.5">
                    Claves modificadas ({validation.diff.changed.length})
                  </p>
                  <ul className="font-mono text-ink-600 dark:text-ink-300 space-y-0.5">
                    {validation.diff.changed.map((k) => (
                      <li key={k} className="text-signal-warn dark:text-amber-300">~ {k}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.diff.unchanged.length > 0 && (
                <div>
                  <p className="font-semibold text-ink-500 mb-0.5">
                    Sin cambios ({validation.diff.unchanged.length})
                  </p>
                  <ul className="font-mono text-ink-400 dark:text-ink-500 space-y-0.5">
                    {validation.diff.unchanged.slice(0, 6).map((k) => (
                      <li key={k}>{k}</li>
                    ))}
                    {validation.diff.unchanged.length > 6 && (
                      <li className="italic">
                        …y {validation.diff.unchanged.length - 6} más
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmación y restaurar */}
        {validation?.valid && (
          <div className="space-y-3">
            <Notice tone="danger">
              <strong>¡Atención!</strong> El <code>.env.production</code> actual será reemplazado
              por el fichero subido. La API se reiniciará. Asegúrate de tener un backup del
              .env actual antes de continuar.
            </Notice>
            <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              Entiendo que esta operación reemplazará el .env de producción
            </label>
            {restoreError && <ErrorNotice error={new Error(restoreError)} />}
            <Button
              variant="danger"
              onClick={() => void handleRestore()}
              disabled={!confirmed || restoring || status === "in_progress"}
            >
              {restoring ? "Encolando…" : "Aplicar nuevo .env"}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Bloque 2: copia fuera del servidor ────────────────────────────────────────

interface RunResponse {
  ok?: boolean;
  running?: boolean;
  fileName?: string;
  sizeBytes?: number;
  retentionNote?: string;
  error?: string;
}

interface TestResponse {
  ok: boolean;
  message: string;
}

const DESTINATION_LABELS: Record<Destination, string> = {
  NONE: "Sin destino",
  SFTP: "Otro servidor (SFTP)",
  S3: "Bucket S3 compatible",
  EMAIL: "Correo electrónico (cifrado)",
};

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : "Error desconocido";
}

interface RemoteListResponse {
  supported: boolean;
  note?: string;
  files: RemoteBackupFile[];
}

interface RemoteDeleteResponse {
  deleted: string[];
  failed: Array<{ name: string; error: string }>;
}

/**
 * Copias guardadas en el destino (SFTP/S3): ver y borrar las que se elijan. Solo se conecta al pulsar
 * «Ver copias» (no en cada carga de la página) y se vuelve a leer si hay una copia nueva.
 */
function RemoteBackupsList({ refreshKey, onDeleted }: { refreshKey: string | null; onDeleted: () => void }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [data, setData] = useState<RemoteListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: "ok" | "warn" | "danger"; text: string } | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<RemoteListResponse>("/api/v1/internal/backup/offsite/files");
      setData(res);
      // Quita de la selección lo que ya no existe.
      setSelected((cur) => new Set([...cur].filter((n) => res.files.some((f) => f.name === n))));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) void load();
  }, [open, refreshKey, load]);

  const files = data?.files ?? [];
  const warning = data ? deletionWarning(files, selected) : null;

  function toggle(name: string) {
    setConfirming(false);
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    setConfirming(false);
    setSelected((cur) => (cur.size === files.length ? new Set() : new Set(files.map((f) => f.name))));
  }

  async function deleteSelected() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    setDeleting(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.post<RemoteDeleteResponse>("/api/v1/internal/backup/offsite/files/delete", {
        names: [...selected],
      });
      if (res.failed.length === 0) {
        setMessage({ tone: "ok", text: `Borradas ${res.deleted.length} copias del destino.` });
      } else {
        setMessage({
          tone: "warn",
          text:
            `Borradas ${res.deleted.length}. No se pudieron borrar ${res.failed.length}: ` +
            res.failed.map((f) => `${f.name} (${f.error})`).join("; "),
        });
      }
      setSelected(new Set());
      await load();
      onDeleted();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-ink-200 pt-4 dark:border-ink-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">Copias guardadas en el destino</h3>
          <p className="text-xs text-ink-500 dark:text-ink-400">
            Aquí puedes borrar las copias de prueba o cualquier otra. La limpieza automática sigue funcionando
            por su cuenta.
          </p>
        </div>
        <Button variant="quiet" onClick={() => setOpen((v) => !v)} disabled={loading || deleting}>
          {open ? "Ocultar" : "Ver copias"}
        </Button>
      </div>

      {open && loading && !data && (
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <Spinner /> Conectando con el destino…
        </div>
      )}

      {open && error && <ErrorNotice error={new Error(error)} />}
      {open && message && <Notice tone={message.tone}>{message.text}</Notice>}

      {open && data && !data.supported && <Notice tone="warn">{data.note}</Notice>}

      {open && data?.supported && files.length === 0 && !loading && (
        <p className="text-sm text-ink-500">No hay copias en el destino.</p>
      )}

      {open && data?.supported && files.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ink-500">
                  <th className="w-8 py-1">
                    <input
                      type="checkbox"
                      aria-label="Elegir todas las copias"
                      checked={selected.size === files.length}
                      onChange={toggleAll}
                      disabled={deleting}
                    />
                  </th>
                  <th className="py-1">Copia</th>
                  <th className="py-1">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.name} className="border-t border-ink-100 dark:border-ink-800">
                    <td className="py-1.5">
                      <input
                        type="checkbox"
                        aria-label={`Elegir ${f.name}`}
                        checked={selected.has(f.name)}
                        onChange={() => toggle(f.name)}
                        disabled={deleting}
                      />
                    </td>
                    <td className="break-all py-1.5 font-mono text-xs">{f.name}</td>
                    <td className="whitespace-nowrap py-1.5 text-xs">{formatDate(f.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {warning && selected.size > 0 && <Notice tone="warn">{warning}</Notice>}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="danger"
              onClick={() => void deleteSelected()}
              disabled={selected.size === 0 || deleting}
            >
              {deleting
                ? "Borrando…"
                : confirming
                  ? `¿Confirmar? Se borrarán ${selected.size} del destino`
                  : `Borrar elegidas (${selected.size})`}
            </Button>
            {confirming && (
              <Button variant="quiet" onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
            )}
            <span className="text-xs text-ink-500">
              {files.length} copias en el destino. El borrado no se puede deshacer.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function OffsiteBackupSection() {
  const api = useApi();
  const [view, setView] = useState<OffsiteView>(EMPTY_VIEW);
  const [form, setForm] = useState<OffsiteForm>(() => formFromView(EMPTY_VIEW));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "test" | "run" | "fingerprint" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: "ok" | "warn" | "danger"; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<OffsiteView>("/api/v1/internal/backup/offsite");
      setView(res);
      return res;
    } catch (err) {
      setLoadError(errMsg(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void (async () => {
      const res = await load();
      if (res) setForm(formFromView(res));
    })();
  }, [load]);

  // Mientras hay una copia en curso, se consulta el estado cada pocos segundos.
  const inProgress = view.last?.status === "RUNNING";
  useEffect(() => {
    if (!inProgress) return;
    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, [inProgress, load]);

  function set<K extends keyof OffsiteForm>(key: K, value: OffsiteForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /** Si cambia el servidor SFTP, la huella anterior deja de valer. */
  function setSftpTarget(key: "sftpHost" | "sftpPort", value: string) {
    setForm((f) => ({ ...f, [key]: value, sftpFingerprint: "", sftpFingerprintConfirmed: false }));
  }

  async function readFingerprint() {
    setError(null);
    setMessage(null);
    const port = Number(form.sftpPort);
    if (!form.sftpHost.trim() || !Number.isInteger(port)) {
      setError("Indica el servidor y el puerto antes de leer la huella.");
      return;
    }
    setBusy("fingerprint");
    try {
      const res = await api.post<{ fingerprint: string }>("/api/v1/internal/backup/offsite/fingerprint", {
        host: form.sftpHost.trim(),
        port,
      });
      setForm((f) => ({ ...f, sftpFingerprint: res.fingerprint, sftpFingerprintConfirmed: false }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setError(null);
    setMessage(null);
    const problem = validateForm(form, view);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy("save");
    try {
      const res = await api.put<OffsiteView>("/api/v1/internal/backup/offsite", buildPayload(form));
      setView(res);
      setForm(formFromView(res));
      setMessage({ tone: "ok", text: "Ajustes guardados." });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  }

  async function testConnection() {
    setError(null);
    setMessage(null);
    setBusy("test");
    try {
      const res = await api.post<TestResponse>("/api/v1/internal/backup/offsite/test", {});
      setMessage({ tone: res.ok ? "ok" : "danger", text: res.message });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  }

  async function runNow() {
    setError(null);
    setMessage(null);
    setBusy("run");
    try {
      const res = await api.post<RunResponse>("/api/v1/internal/backup/offsite/run", {});
      if (res.running) {
        setMessage({ tone: "warn", text: "La copia sigue en marcha; el estado se actualizará solo." });
      } else if (res.ok) {
        setMessage({
          tone: "ok",
          text: `Copia enviada: ${res.fileName ?? ""} (${formatBytesShort(res.sizeBytes)}).${res.retentionNote ? ` ${res.retentionNote}` : ""}`,
        });
      } else {
        setMessage({ tone: "danger", text: res.error ?? "La copia falló." });
      }
      await load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="flex items-center gap-2 p-5 text-sm text-ink-500">
          <Spinner /> Cargando copia fuera del servidor…
        </div>
      </Card>
    );
  }

  const status = lastStatus(view);
  const last = view.last;
  const unsaved = form.destination !== view.destination;

  return (
    <Card>
      <div className="space-y-5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="micro-label">Copia fuera del servidor</h2>
            <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
              Cada día se genera un volcado de la base de datos y se envía a otro lugar, para poder recuperarse si
              se pierde este servidor. Es opcional.
            </p>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              ¿Tienes una copia cifrada (.enc) que abrir?{" "}
              <a className="underline" href="/herramienta-descifrar-backup.html" download="descifrar-backup.html">
                Descargar la herramienta de descifrado
              </a>{" "}
              (se abre con el navegador, sin instalar nada ni conexión).
            </p>
          </div>
          <Chip tone={status.tone}>{status.label}</Chip>
        </div>

        {loadError && <ErrorNotice error={new Error(loadError)} onRetry={() => void load()} />}

        {!view.encryptionKeyConfigured && (
          <Notice tone="danger">
            El servidor no tiene configurada la clave que protege las contraseñas guardadas (SMTP_ENCRYPTION_KEY).
            Sin ella no se pueden guardar credenciales de SFTP ni de S3.
          </Notice>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          Hacer la copia automáticamente cada día
        </label>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Hora (del servidor)" htmlFor="offsite-time">
            <Input id="offsite-time" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} />
          </Field>
          <Field
            label="Días que se conservan"
            hint={`Entre 3 y 3650. Siempre se guardan las ${view.minKeep} últimas.`}
            htmlFor="offsite-retention"
          >
            <Input
              id="offsite-retention"
              type="number"
              min={3}
              max={3650}
              value={form.retentionDays}
              onChange={(e) => set("retentionDays", e.target.value)}
            />
          </Field>
          <Field label="Destino" htmlFor="offsite-destination">
            <Select
              id="offsite-destination"
              value={form.destination}
              onChange={(e) => set("destination", e.target.value as Destination)}
            >
              {(Object.keys(DESTINATION_LABELS) as Destination[]).map((d) => (
                <option key={d} value={d}>
                  {DESTINATION_LABELS[d]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {form.destination === "SFTP" && (
          <fieldset className="space-y-4 rounded-lg border border-ink-200 p-4 dark:border-ink-700">
            <legend className="micro-label px-1">Servidor SFTP</legend>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Field label="Servidor" htmlFor="sftp-host">
                  <Input
                    id="sftp-host"
                    value={form.sftpHost}
                    onChange={(e) => setSftpTarget("sftpHost", e.target.value)}
                    placeholder="backup.midominio.es"
                  />
                </Field>
              </div>
              <Field label="Puerto" htmlFor="sftp-port">
                <Input
                  id="sftp-port"
                  type="number"
                  value={form.sftpPort}
                  onChange={(e) => setSftpTarget("sftpPort", e.target.value)}
                />
              </Field>
              <Field label="Usuario" htmlFor="sftp-user">
                <Input id="sftp-user" value={form.sftpUsername} onChange={(e) => set("sftpUsername", e.target.value)} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Carpeta de destino" htmlFor="sftp-dir">
                  <Input id="sftp-dir" value={form.sftpDirectory} onChange={(e) => set("sftpDirectory", e.target.value)} />
                </Field>
              </div>
              <Field
                label="Contraseña"
                hint={view.sftp?.hasPassword ? "Ya hay una guardada; déjala vacía para conservarla." : undefined}
                htmlFor="sftp-pass"
              >
                <Input
                  id="sftp-pass"
                  type="password"
                  autoComplete="new-password"
                  value={form.sftpPassword}
                  onChange={(e) => set("sftpPassword", e.target.value)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field
                  label="o clave privada (opcional)"
                  hint={view.sftp?.hasPrivateKey ? "Ya hay una guardada; déjala vacía para conservarla." : undefined}
                  htmlFor="sftp-key"
                >
                  <Textarea
                    id="sftp-key"
                    rows={3}
                    spellCheck={false}
                    value={form.sftpPrivateKey}
                    onChange={(e) => set("sftpPrivateKey", e.target.value)}
                    placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  />
                </Field>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="quiet" onClick={() => void readFingerprint()} disabled={busy !== null}>
                  {busy === "fingerprint" ? "Leyendo…" : "Leer huella del servidor"}
                </Button>
                {form.sftpFingerprint && <code className="break-all text-xs">{form.sftpFingerprint}</code>}
              </div>
              {form.sftpFingerprint && (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.sftpFingerprintConfirmed}
                    onChange={(e) => set("sftpFingerprintConfirmed", e.target.checked)}
                  />
                  Confirmo que esta huella es la de mi servidor (compárala con la que muestra el propio servidor).
                  Así se evita enviar copias a un equipo suplantado.
                </label>
              )}
            </div>
          </fieldset>
        )}

        {form.destination === "S3" && (
          <fieldset className="space-y-4 rounded-lg border border-ink-200 p-4 dark:border-ink-700">
            <legend className="micro-label px-1">Bucket S3 compatible</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Endpoint (https)" hint="Déjalo vacío para Amazon S3." htmlFor="s3-endpoint">
                <Input
                  id="s3-endpoint"
                  value={form.s3Endpoint}
                  onChange={(e) => set("s3Endpoint", e.target.value)}
                  placeholder="https://s3.eu-central-003.backblazeb2.com"
                />
              </Field>
              <Field label="Región" htmlFor="s3-region">
                <Input id="s3-region" value={form.s3Region} onChange={(e) => set("s3Region", e.target.value)} />
              </Field>
              <Field label="Bucket" htmlFor="s3-bucket">
                <Input id="s3-bucket" value={form.s3Bucket} onChange={(e) => set("s3Bucket", e.target.value)} />
              </Field>
              <Field label="Carpeta (prefijo)" htmlFor="s3-prefix">
                <Input id="s3-prefix" value={form.s3Prefix} onChange={(e) => set("s3Prefix", e.target.value)} />
              </Field>
              <Field label="Clave de acceso (ID)" htmlFor="s3-key">
                <Input id="s3-key" value={form.s3AccessKeyId} onChange={(e) => set("s3AccessKeyId", e.target.value)} />
              </Field>
              <Field
                label="Clave secreta"
                hint={view.s3?.hasSecret ? "Ya hay una guardada; déjala vacía para conservarla." : undefined}
                htmlFor="s3-secret"
              >
                <Input
                  id="s3-secret"
                  type="password"
                  autoComplete="new-password"
                  value={form.s3SecretAccessKey}
                  onChange={(e) => set("s3SecretAccessKey", e.target.value)}
                />
              </Field>
            </div>
          </fieldset>
        )}

        {form.destination === "EMAIL" && (
          <fieldset className="space-y-3 rounded-lg border border-ink-200 p-4 dark:border-ink-700">
            <legend className="micro-label px-1">Envío por correo</legend>
            <Field label="Destinatarios (hasta 5, separados por coma)" htmlFor="email-to">
              <Input
                id="email-to"
                value={form.emailTo}
                onChange={(e) => set("emailTo", e.target.value)}
                placeholder="copias@midominio.es"
              />
            </Field>
            <p className="text-xs text-ink-500 dark:text-ink-400">
              Se usa el SMTP del servidor. La copia va adjunta y siempre cifrada (máximo 20 MB). Un correo ya
              enviado no se puede borrar, así que aquí no se aplica la retención: limpia tu buzón cuando quieras.
            </p>
          </fieldset>
        )}

        {form.destination !== "NONE" && (
          <fieldset className="space-y-3 rounded-lg border border-ink-200 p-4 dark:border-ink-700">
            <legend className="micro-label px-1">Cifrado</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.encryptEnabled}
                onChange={(e) => set("encryptEnabled", e.target.checked)}
              />
              Cifrar la copia antes de enviarla
              {form.destination === "EMAIL" && <span className="text-xs text-ink-500">(obligatorio por correo)</span>}
            </label>
            {form.encryptEnabled && (
              <>
                <Field
                  label="Clave pública RSA (PEM)"
                  hint="Se cifra con tu clave pública; el servidor nunca podrá descifrarla. Guarda la privada fuera de aquí."
                  htmlFor="encrypt-key"
                >
                  <Textarea
                    id="encrypt-key"
                    rows={4}
                    spellCheck={false}
                    value={form.encryptPublicKey}
                    onChange={(e) => set("encryptPublicKey", e.target.value)}
                    placeholder="-----BEGIN PUBLIC KEY-----"
                  />
                </Field>
                {view.encryptKeyFingerprint && (
                  <p className="text-xs">
                    Clave guardada: <code className="break-all">{view.encryptKeyFingerprint}</code>
                    {form.encryptPublicKey.trim() ? " (se sustituirá al guardar)" : ""}
                  </p>
                )}
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  Si pierdes la clave privada, las copias cifradas no se podrán recuperar. La herramienta de descifrado
                  (enlace en la cabecera de este bloque) se abre con el navegador, funciona sin conexión y ni la
                  copia ni la clave salen de tu equipo.
                </p>
              </>
            )}
          </fieldset>
        )}

        {unsaved && view.destination !== "NONE" && (
          <Notice tone="warn">Has cambiado el destino: guarda antes de probar o de hacer una copia.</Notice>
        )}

        <div className="flex flex-wrap gap-3">
          <Button variant="primary" onClick={() => void save()} disabled={busy !== null}>
            {busy === "save" ? "Guardando…" : "Guardar ajustes"}
          </Button>
          <Button
            variant="quiet"
            onClick={() => void testConnection()}
            disabled={busy !== null || view.destination === "NONE"}
          >
            {busy === "test" ? "Probando…" : "Probar conexión"}
          </Button>
          <Button
            variant="quiet"
            onClick={() => void runNow()}
            disabled={busy !== null || inProgress || view.destination === "NONE"}
          >
            {busy === "run" || inProgress ? "Copiando…" : "Hacer copia ahora"}
          </Button>
        </div>

        {error && <ErrorNotice error={new Error(error)} />}
        {message && <Notice tone={message.tone}>{message.text}</Notice>}

        {last && (
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <div>
              <dt className="inline text-ink-500">Último intento: </dt>
              <dd className="inline">{last.attemptAt ? formatDate(last.attemptAt) : "—"}</dd>
            </div>
            <div>
              <dt className="inline text-ink-500">Última correcta: </dt>
              <dd className="inline">{last.successAt ? formatDate(last.successAt) : "—"}</dd>
            </div>
            <div>
              <dt className="inline text-ink-500">Archivo: </dt>
              <dd className="inline break-all">
                {last.fileName ? `${last.fileName} (${formatBytesShort(last.sizeBytes)})` : "— (ya no está en el destino)"}
              </dd>
            </div>
            {last.retentionNote && (
              <div>
                <dt className="inline text-ink-500">Limpieza: </dt>
                <dd className="inline">{last.retentionNote}</dd>
              </div>
            )}
            {last.error && (
              <div className="sm:col-span-2 text-signal-danger dark:text-red-300">
                Error ({last.consecutiveFailures} seguidos): {last.error}
              </div>
            )}
          </dl>
        )}

        {(view.destination === "SFTP" || view.destination === "S3") && !unsaved && (
          <RemoteBackupsList refreshKey={last?.successAt ?? null} onDeleted={() => void load()} />
        )}
      </div>
    </Card>
  );
}

// ── Raíz ──────────────────────────────────────────────────────────────────────

export function BackupPage() {
  return (
    <>
      <PageHeader
        eyebrow="Sistema"
        title="Backup y restauración"
        description="Gestiona los volcados de base de datos (también su copia fuera del servidor) y las variables de entorno. Estas operaciones afectan a toda la plataforma."
      />

      <div className="space-y-6">
        {/* Aviso general */}
        <Notice tone="warn">
          Las operaciones de restauración son <strong>irreversibles</strong>. Genera siempre un
          backup antes de restaurar. La API reiniciará automáticamente tras cada restauración (el
          guardian lo gestiona sin intervención manual).
        </Notice>

        <BackupDbSection />
        <OffsiteBackupSection />
        <RestoreDbSection />
        <BackupEnvSection />
        <RestoreEnvSection />
      </div>
    </>
  );
}
